/**
 * Analisi consumi per fascia oraria GSE — F1/F2/F3 (SLIMS-CER-036).
 *
 * Le fasce sono definite dal TIAD (Delibera ARERA 727/2022/R/eel, che riprende
 * la 181/06) sul **calendario civile italiano**, non su una quota fissa:
 *
 *   F1 Punta       Lun-Ven 8:00-19:00, esclusi i festivi nazionali
 *   F2 Intermedia  Lun-Ven 7:00-8:00 e 19:00-23:00; Sab 7:00-23:00, esclusi i festivi
 *   F3 Fuori punta Lun-Sab 0:00-7:00 e 23:00-24:00; domeniche e festivi tutto il giorno
 *
 * Finché non c'è misura, l'unica ripartizione possibile è la quota statistica
 * (profili TERNA, `TIME_BANDS`). Ma la serie oraria riconciliata di
 * `energy_shared` (GSE > reale > stimato > tipico) porta con sé il timestamp
 * di ogni ora: dove c'è misura la fascia si **classifica**, non si stima. Le due
 * cose non vanno confuse in UI, quindi il risultato dichiara sempre con
 * `measured` da dove arrivano le quote.
 *
 * Qui non si fa I/O: le pagine che mostrano le fasce hanno già la serie in
 * memoria per i KPI, esattamente come per il trend consumi.
 */

import { TIME_BANDS } from '../constants/gse-tariffs';

export type Fascia = 'F1' | 'F2' | 'F3';

/** Le tre fasce in ordine di presentazione. */
export const FASCE: readonly Fascia[] = ['F1', 'F2', 'F3'] as const;

// ─── Calendario ─────────────────────────────────────────────────────────────

const MS_PER_HOUR = 3_600_000;

/**
 * Ultima domenica del mese (0-indexed) a mezzanotte UTC.
 * `Date.UTC(year, month + 1, 0)` è l'ultimo giorno del mese richiesto.
 */
function lastSundayUtc(year: number, month: number): Date {
	const date = new Date(Date.UTC(year, month + 1, 0));
	date.setUTCDate(date.getUTCDate() - date.getUTCDay());
	return date;
}

/**
 * Offset dell'ora italiana: CET (+1) o CEST (+2).
 *
 * L'ora legale in UE va dall'ultima domenica di marzo alle 01:00 UTC
 * all'ultima domenica di ottobre alle 01:00 UTC (Direttiva 2000/84/CE). La
 * regola è deterministica e si calcola in aritmetica di date: preferita a
 * `Intl.DateTimeFormat` perché qui si classificano anche 8.760 punti per volta
 * dentro una `useMemo`, e perché il risultato non deve dipendere dal fuso del
 * runtime (browser dell'utente, Worker in UTC o CI).
 */
function romeOffsetHours(instant: Date): number {
	const year = instant.getUTCFullYear();
	const dstStart = lastSundayUtc(year, 2); // marzo
	dstStart.setUTCHours(1, 0, 0, 0);
	const dstEnd = lastSundayUtc(year, 9); // ottobre
	dstEnd.setUTCHours(1, 0, 0, 0);
	return instant >= dstStart && instant < dstEnd ? 2 : 1;
}

/**
 * L'istante traslato all'ora civile italiana, da leggere con i getter **UTC**
 * (`getUTCHours`, `getUTCDay`, …). Traslare e leggere in UTC evita di ereditare
 * il fuso locale del runtime.
 */
function toRome(instant: Date): Date {
	return new Date(instant.getTime() + romeOffsetHours(instant) * MS_PER_HOUR);
}

/**
 * Domenica di Pasqua (calendario gregoriano) — algoritmo "anonimo"/Meeus.
 * Serve solo per il lunedì dell'Angelo: la domenica di Pasqua è già F3 in quanto
 * domenica.
 */
function easterUtc(year: number): Date {
	const a = year % 19;
	const b = Math.floor(year / 100);
	const c = year % 100;
	const d = Math.floor(b / 4);
	const e = b % 4;
	const f = Math.floor((b + 8) / 25);
	const g = Math.floor((b - f + 1) / 3);
	const h = (19 * a + b - d - g + 15) % 30;
	const i = Math.floor(c / 4);
	const k = c % 4;
	const l = (32 + 2 * e + 2 * i - h - k) % 7;
	const m = Math.floor((a + 11 * h + 22 * l) / 451);
	const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = marzo, 4 = aprile
	const day = ((h + l - 7 * m + 114) % 31) + 1;
	return new Date(Date.UTC(year, month - 1, day));
}

/** Festività nazionali a data fissa, come `MM-DD`. */
const FIXED_HOLIDAYS = new Set([
	'01-01', // Capodanno
	'01-06', // Epifania
	'04-25', // Liberazione
	'05-01', // Festa dei lavoratori
	'06-02', // Festa della Repubblica
	'08-15', // Ferragosto
	'11-01', // Ognissanti
	'12-08', // Immacolata
	'12-25', // Natale
	'12-26', // Santo Stefano
]);

/** Cache per anno del lunedì dell'Angelo (`MM-DD`): il calcolo è puro. */
const easterMondayCache = new Map<number, string>();

function easterMondayKey(year: number): string {
	const cached = easterMondayCache.get(year);
	if (cached !== undefined) return cached;
	const monday = easterUtc(year);
	monday.setUTCDate(monday.getUTCDate() + 1);
	const key = `${String(monday.getUTCMonth() + 1).padStart(2, '0')}-${String(
		monday.getUTCDate(),
	).padStart(2, '0')}`;
	easterMondayCache.set(year, key);
	return key;
}

/**
 * Festivo nazionale italiano ai fini TIAD. `romeDate` è già traslato all'ora
 * civile italiana. Il santo patrono non conta: le fasce ARERA usano solo le
 * festività nazionali, uguali su tutto il territorio.
 */
function isItalianHoliday(romeDate: Date): boolean {
	const key = `${String(romeDate.getUTCMonth() + 1).padStart(2, '0')}-${String(
		romeDate.getUTCDate(),
	).padStart(2, '0')}`;
	return FIXED_HOLIDAYS.has(key) || key === easterMondayKey(romeDate.getUTCFullYear());
}

/**
 * La regola TIAD applicata a un giorno civile italiano già risolto e alla sua
 * ora civile. Separata dalla conversione di fuso perché i due ingressi arrivano
 * da strade diverse: da un istante UTC (serie oraria misurata) o da una coppia
 * giorno/ora già in ora locale (curve previsionali, indicizzate 0-23).
 */
function fasciaOf(romeDate: Date, hour: number): Fascia {
	const weekday = romeDate.getUTCDay(); // 0 = domenica … 6 = sabato

	// Domeniche e festivi: F3 tutto il giorno.
	if (weekday === 0 || isItalianHoliday(romeDate)) return 'F3';
	// Sabato: F2 dalle 7 alle 23, F3 il resto.
	if (weekday === 6) return hour >= 7 && hour < 23 ? 'F2' : 'F3';
	// Lun-Ven.
	if (hour >= 8 && hour < 19) return 'F1';
	if (hour === 7 || (hour >= 19 && hour < 23)) return 'F2';
	return 'F3';
}

/**
 * Fascia TIAD dell'istante dato. L'ora di riferimento è quella di **inizio**
 * del bucket orario, coerente con `energy_shared` che tronca a `date_trunc('hour')`.
 */
export function classifyFascia(instant: Date | string | number): Fascia {
	const date = instant instanceof Date ? instant : new Date(instant);
	if (Number.isNaN(date.getTime())) return 'F3';
	const rome = toRome(date);
	return fasciaOf(rome, rome.getUTCHours());
}

/**
 * Fascia TIAD dell'ora civile `hour` (0-23) del giorno civile `date`
 * ('YYYY-MM-DD', Europe/Rome).
 *
 * Le curve previsionali sono già indicizzate sull'ora locale: ricostruire un
 * istante UTC per poi riconvertirlo indietro reintrodurrebbe l'ora legale in un
 * punto in cui non c'è, e nei due giorni di cambio ora sposterebbe la fascia di
 * un'ora. Giorno della settimana e festività si leggono dalla data civile, che è
 * tutto ciò che la regola ARERA guarda oltre all'ora.
 */
export function classifyFasciaAtHour(date: string, hour: number): Fascia {
	const civil = new Date(`${date}T00:00:00Z`);
	if (Number.isNaN(civil.getTime())) return 'F3';
	return fasciaOf(civil, hour);
}

// ─── Ripartizione dei consumi ───────────────────────────────────────────────

/** Un bucket della serie oraria riconciliata (`GET /api/energy/shared`). */
export interface TimeBandPoint {
	hour: string | Date;
	totalImmessa: number;
	totalPrelevata: number;
	energiaCondivisa: number;
}

export interface TimeBandSlice {
	fascia: Fascia;
	/** `Punta`, `Intermedia`, `Fuori Punta`. */
	label: string;
	/** Definizione oraria della fascia, per tooltip e footnote. */
	description: string;
	/** Consumo (energia prelevata) attribuito alla fascia, kWh. */
	prelevata: number;
	/** Energia condivisa nelle ore della fascia, kWh. */
	condivisa: number;
	/** Energia immessa nelle ore della fascia, kWh. */
	immessa: number;
	/** Quota di consumo della fascia sul totale, in % (0-100, arrotondata a 0.1). */
	pct: number;
	/** Ore di misura ricadute nella fascia. `0` quando le quote sono statistiche. */
	hours: number;
}

export interface TimeBandBreakdown {
	/** Sempre tre elementi, in ordine F1, F2, F3. */
	bands: TimeBandSlice[];
	byFascia: Record<Fascia, TimeBandSlice>;
	totalPrelevata: number;
	totalCondivisa: number;
	totalImmessa: number;
	/**
	 * `true` = quote classificate dai timestamp reali della serie oraria.
	 * `false` = quote statistiche TERNA applicate a un totale (nessuna misura).
	 * La UI deve dichiararlo: sono due numeri di natura diversa.
	 */
	measured: boolean;
	/** Ore di misura complessive considerate. */
	hoursMeasured: number;
}

export interface TimeBandOptions {
	/**
	 * Consumo totale kWh su cui applicare le quote statistiche quando la serie
	 * oraria è vuota (es. stima da profili dichiarati in onboarding).
	 */
	fallbackTotalKwh?: number;
}

function emptySlice(fascia: Fascia): TimeBandSlice {
	return {
		fascia,
		label: TIME_BANDS[fascia].label,
		description: TIME_BANDS[fascia].description,
		prelevata: 0,
		condivisa: 0,
		immessa: 0,
		pct: 0,
		hours: 0,
	};
}

function round1(value: number): number {
	return Math.round(value * 10) / 10;
}

/**
 * Ripartisce consumo, energia condivisa ed energia immessa sulle tre fasce TIAD.
 *
 * Con misura disponibile ogni ora finisce nella fascia che le compete per
 * calendario; senza misura si ricade sulle quote statistiche TERNA applicate a
 * `fallbackTotalKwh`, e `measured` resta `false`.
 */
export function buildTimeBandBreakdown(
	points: readonly TimeBandPoint[],
	options: TimeBandOptions = {},
): TimeBandBreakdown {
	const byFascia: Record<Fascia, TimeBandSlice> = {
		F1: emptySlice('F1'),
		F2: emptySlice('F2'),
		F3: emptySlice('F3'),
	};

	let hoursMeasured = 0;
	for (const point of points) {
		const date = point.hour instanceof Date ? point.hour : new Date(point.hour);
		if (Number.isNaN(date.getTime())) continue;
		const slice = byFascia[classifyFascia(date)];
		slice.prelevata += point.totalPrelevata;
		slice.condivisa += point.energiaCondivisa;
		slice.immessa += point.totalImmessa;
		slice.hours += 1;
		hoursMeasured += 1;
	}

	const measuredPrelevata = FASCE.reduce((sum, f) => sum + byFascia[f].prelevata, 0);

	// Nessuna ora misurata (o tutte a zero): quote statistiche sul totale stimato.
	if (hoursMeasured === 0 || measuredPrelevata <= 0) {
		const fallback = Math.max(0, options.fallbackTotalKwh ?? 0);
		for (const fascia of FASCE) {
			const slice = byFascia[fascia];
			slice.prelevata = Math.round(fallback * TIME_BANDS[fascia].share);
			slice.condivisa = 0;
			slice.immessa = 0;
			slice.hours = 0;
			slice.pct = round1(TIME_BANDS[fascia].share * 100);
		}
		return {
			bands: FASCE.map((f) => byFascia[f]),
			byFascia,
			totalPrelevata: FASCE.reduce((sum, f) => sum + byFascia[f].prelevata, 0),
			totalCondivisa: 0,
			totalImmessa: 0,
			measured: false,
			hoursMeasured: 0,
		};
	}

	for (const fascia of FASCE) {
		const slice = byFascia[fascia];
		slice.pct = round1((slice.prelevata / measuredPrelevata) * 100);
		slice.prelevata = Math.round(slice.prelevata);
		slice.condivisa = Math.round(slice.condivisa);
		slice.immessa = Math.round(slice.immessa);
	}

	return {
		bands: FASCE.map((f) => byFascia[f]),
		byFascia,
		totalPrelevata: FASCE.reduce((sum, f) => sum + byFascia[f].prelevata, 0),
		totalCondivisa: FASCE.reduce((sum, f) => sum + byFascia[f].condivisa, 0),
		totalImmessa: FASCE.reduce((sum, f) => sum + byFascia[f].immessa, 0),
		measured: true,
		hoursMeasured,
	};
}
